import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import { CREATIVE_SNAPSHOT_HEADER, CREATIVE_SNAPSHOT_REFRESH_HEADER, type CreativePerformanceSnapshotMetadata } from "@shared/creative-performance-snapshot";

export type CreativeSavedData<T> = T & { snapshot?: CreativePerformanceSnapshotMetadata };
const forced = new Set<string>();
const pollStarted = new Map<string, number>();
export function isCreativeFeedQuery(queryKey: QueryKey, brandId: string) {
  return typeof queryKey[0] === "string"
    && queryKey[0].startsWith(`/api/brands/${brandId}/creative-performance/`)
    && /\/(ads|trend|funnel|persona-trend)\?/.test(queryKey[0]);
}
export function useCreativeFeedScopeError(brandId: string) {
  const client = useQueryClient();
  const subscribe = useCallback((changed: () => void) => client.getQueryCache().subscribe(changed), [client]);
  const getSnapshot = useCallback(() => {
    const queries = client.getQueryCache().findAll({ predicate: query => isCreativeFeedQuery(query.queryKey, brandId) });
    const retrieved = Math.max(0, ...queries.map(query => {
      const fetchedAt = (query.state.data as CreativeSavedData<unknown> | undefined)?.snapshot?.fetchedAt;
      return fetchedAt ? Date.parse(fetchedAt) || 0 : 0;
    }));
    // A fatal drill-down error remains authoritative after its observer
    // unmounts. Only a later upstream retrieval can restore the scope.
    return (queries.find(query => (query.state.error as (Error & { fatal?: boolean }) | null)?.fatal
      && query.state.errorUpdatedAt >= retrieved)?.state.error as Error | undefined) ?? null;
  }, [client, brandId]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
export async function refreshCreativePerformance(client: QueryClient, brandId: string) {
  // Only mounted, enabled dependencies for this view; no saved filters change.
  const queries = client.getQueryCache().findAll({ predicate: query => query.isActive() && isCreativeFeedQuery(query.queryKey, brandId) });
  for (const query of queries) {
    const url = String(query.queryKey[0]);
    forced.add(url);
    pollStarted.delete(url);
  }
  await client.refetchQueries({ predicate: query => queries.includes(query), type: "active" }, { cancelRefetch: false });
}
export function creativeFeedQueryOptions<T>() {
  return {
    structuralSharing: false as const,
    queryFn: async ({ queryKey, signal }: { queryKey: QueryKey; signal: AbortSignal }): Promise<T> => {
      const url = String(queryKey[0]);
      const force = forced.delete(url);
      const response = await fetch(url, { credentials: "include", signal,
        headers: force ? { [CREATIVE_SNAPSHOT_REFRESH_HEADER]: "true" } : {} });
      if (!response.ok) {
        const text = await response.text();
        const error = new Error(`${response.status}: ${text}`) as Error & { fatal?: boolean };
        try {
          const body = JSON.parse(text);
           error.fatal = response.status === 401 || response.status === 403 || body.notConfigured || body.sourceUnavailable
            || body.upstreamStatus === 401 || body.upstreamStatus === 403;
        } catch { /* Preserve the original error contract. */ }
        throw error;
      }
      const data = await response.json() as CreativeSavedData<T>;
      const header = response.headers.get(CREATIVE_SNAPSHOT_HEADER);
      if (header) {
        try {
          data.snapshot = JSON.parse(header);
          if (data.snapshot?.state === "refreshing") {
            if (!pollStarted.has(url)) pollStarted.set(url, Date.now());
          } else pollStarted.delete(url);
        } catch { /* Older servers may not send retrieval metadata. */ }
      }
      return data;
    },
    refetchInterval: (query: { queryKey: QueryKey; state: { data: unknown; error: unknown } }) => {
      const data = query.state.data as CreativeSavedData<T> | undefined;
      const started = pollStarted.get(String(query.queryKey[0]));
      return !query.state.error && data?.snapshot?.state === "refreshing" && started !== undefined && Date.now() - started < 120_000
        ? 2000 : false;
    },
  };
}
